// @flow

import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { colors, sizes } from '../../../styles/GlobalStyle';
import { ActionLink } from './ActionLink';
import { __ } from '../../../i18n/l10n';

type CheckboxRawProps = {
  className?: string,
  openModal?: Function,
  error: Object,
  label: string,
  labelLink?: string,
  onChange?: Function,
  name: string,
  id: string,
};

const CheckboxRaw = forwardRef<CheckboxRawProps, HTMLInputElement>(
  ({ className, openModal, error, label, labelLink, onChange, name, id }, ref) => {
    const innerRef = useRef(null);

    useImperativeHandle(ref, () => innerRef.current);

    const handleKeyPress = (event) => {
      if (innerRef.current && event.key === 'Enter') {
        innerRef.current.checked = !innerRef.current.checked;
        if (onChange) onChange(innerRef.current.checked);
      }
    };

    return (
      <div className={className}>
        <div className="checkbox-cont">
          <input
            className="inp-cbx"
            name={name}
            id={id}
            type="checkbox"
            ref={innerRef}
            onChange={onChange}
          />
          <label className="cbx" htmlFor={id} onKeyPress={handleKeyPress}>
            <span>
              <svg width="12px" height="10px" viewBox="0 0 12 10">
                <polyline points="1.5 6 4.5 9 10.5 1" />
              </svg>
            </span>
            <span>
              {!labelLink ? (
                label
              ) : (
                <>
                  {label}
                  <ActionLink onClick={openModal}>{labelLink}</ActionLink>
                </>
              )}
            </span>
          </label>
        </div>
        {error && <div className="error error--chk">{__(error.message)}</div>}
      </div>
    );
  }
);

CheckboxRaw.displayName = 'CheckBox';

export const Checkbox: StyledComponent<CheckboxRawProps, {}, typeof CheckboxRaw> = styled(
  CheckboxRaw
)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: calc(${sizes.padding} * 2);

  .checkbox-cont {
    display: flex;
    align-items: center;
  }

  .cbx {
    user-select: none;
    cursor: pointer;
    display: flex;
    width: 100%;
    align-items: flex-start;
    font-weight: 900;
    text-transform: uppercase;
    outline: 0;
    &:focus {
      span:first-child {
        box-shadow: 1px 1px 5px 2px ${colors.yellow};
      }
    }
  }

  .cbx span {
    display: inline-block;
    vertical-align: middle;
    transform: translate3d(0, 0, 0);
  }

  .cbx span:first-child {
    position: relative;
    width: 18px;
    height: 18px;
    border-radius: 3px;
    transform: scale(1);
    vertical-align: middle;
    border: 1px solid ${colors.yellow};
    transition: all 0.2s ease;
  }

  .cbx span:first-child svg {
    position: absolute;
    top: 3px;
    left: 2px;
    fill: none;
    stroke: #ffffff;
    stroke-width: 0;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 16px;
    stroke-dashoffset: 16px;
    transition: all 0.3s ease;
    transition-delay: 0.1s;
    transform: translate3d(0, 0, 0);
  }

  .cbx span:first-child:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${colors.yellow};
    display: block;
    transform: scale(0);
    opacity: 1;
    border-radius: 50%;
  }

  .cbx span:last-child {
    flex: 1;
    padding-left: 8px;
  }

  .cbx:hover span:first-child {
    border-color: ${colors.yellow};
  }

  .inp-cbx {
    position: absolute;
    left: -100000;
    opacity: 0;
  }

  .inp-cbx:focus + .cbx span:first-child {
    border-color: ${colors.white};
  }

  .inp-cbx:checked + .cbx span:first-child {
    background: ${colors.yellow};
    border-color: ${colors.yellow};
    animation: wave 0.4s ease;
  }

  .inp-cbx:checked + .cbx span:first-child svg {
    stroke-dashoffset: 0;
    stroke-width: 2;
  }

  .inp-cbx:checked + .cbx span:first-child:before {
    transform: scale(3.5);
    opacity: 0;
    transition: all 0.6s ease;
  }

  @keyframes wave {
    50% {
      transform: scale(0.9);
    }
  }

  .error--chk {
    padding-left: 25px;
  }
`;
